import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import moment from 'moment';

import LoadingTable from '../Components/LoadingTable';
import { getOrgs } from 'redux/actions/Org';

import ActionDialog from 'views/Components/ActionDialog';
import SimpleModelForm from 'views/Components/SimpleModelForm';

import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableRow from '@material-ui/core/TableRow';
import Toolbar from '@material-ui/core/Toolbar';
import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import DeleteIcon from '@material-ui/icons/DeleteOutline';
import EditIcon from '@material-ui/icons/Edit';

import DialogTitle from '@material-ui/core/DialogTitle';
import Dialog from '@material-ui/core/Dialog';

import countries from 'i18n-iso-countries';
countries.registerLocale(require("i18n-iso-countries/langs/en.json"));

const useStyles = makeStyles((theme) => ({
  titleBar: {
    width: 'fit-content',
    flex: '1 1 100%',
    '& > *': {
      margin: theme.spacing(1),
    },
  },
  orgName: {
    paddingBottom: theme.spacing(4),
  },
  toolBar: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
  tableLeftHeaderCell: {
    'text-align': 'right',
    'font-weight': 700,
    'white-space': 'nowrap',
  },
}));

const ORG_TYPES = [
    {value: 'normal', label: 'Paying Customer'},
    {value: 'poc', label: 'Customer Trial'},
    {value: 'demo', label: 'Internal Blumira Demo Account'},
    {value: 'internal', label: 'Internal'},
];

const MARKETS = [
    {value: 0, label: 'Direct'},
    {value: 10, label: 'MSSP'},
    {value: 20, label: 'MSP'},
    {value: 30, label: 'Self-Service'},
    {value: 40, label: 'Reseller'},
    {value: 50, label: 'Referral'}
];

const LICENSE_TYPES = [
  {value: 'FREE', label: 'Free'},
  {value: 'M365', label: 'Microsoft 365'},
  {value: 'CLOUD', label: 'Cloud'},
  {value: 'ADVANCED', label: 'Advanced'},
];

const getUserTimezone = () => {
  let userTz = null;

  try {
    const payload = JSON.parse(localStorage.getItem('idTokenPayload'));
    userTz = payload['https://b5a.io/geoip'].time_zone;
  } catch (err) {
    userTz = moment.tz.guess();
  }

  return userTz;
};

export const EditOrgDialog = (props) => {
  const {
    isSaving,
    openForm, 
    userModel, 
    handleClose, 
    isSuperadmin,
    locationModel, 
    timezones = [], 
    handleSaveClick, 
    selectedOrgModel
  } = props;

  const timezoneOptions = timezones
      .map(tz => ({value: tz.name, label: tz.name}))
      .sort((a, b) => a.label.localeCompare(b.label))

  // FIELDS
  const organizationFields = !isSuperadmin ? {
    'name': {
      type: 'text',
      default: '',
      label: 'Name',
    },
    'shortName': {
      xs: 6,
      type: 'text',
      default: '',
      label: 'Short Name',
    },
    'webUrl': {
      xs: 6,
      type: 'text',
      default: '',
      label: 'Web Domain',
    },
  } : {
    'name': {
      type: 'text',
      default: '',
      label: 'Name',
    },
    'shortName': {
      xs: 6,
      type: 'text',
      default: '',
      label: 'Short Name',
    },
    'webUrl': {
      xs: 6,
      type: 'text',
      default: '',
      label: 'Web Domain',
    },
    'orgType': {
      xs: 6,
      type: 'select',
      lookup: ORG_TYPES,
      default: 'poc',
      label: 'Account Type',
    },
    'market': {
      xs: 6,
      type: 'select',
      lookup: MARKETS,
      default: 0,
      label: 'Market',
    },
    'license': {
      type: 'select',
      lookup: LICENSE_TYPES,
      label: 'License',
    },
    'notes': {
      type: 'text',
      default: '',
      label: 'Internal Notes'
    }
  }

  const locationFields = {
    'name': {
      xs: 6,
      type: 'text',
      default: 'Headquarters',
      label: 'Name'
    },
    'timezone': {
      xs: 6,
      type: 'select',
      label: 'Timezone',
      lookup: timezoneOptions,
      default: getUserTimezone()
    },
    'city': {
      xs: 6,
      type: 'text',
      default: '',
      label: 'City'
    },
    'country': {
      xs: 6,
      type: 'select',
      default: 'US',
      label: 'Country',
      lookup: Object.entries(countries.getNames('en'))
          .map(([key, val]) => ({value: key, label: val}))
          .sort((a, b) => a.label.localeCompare(b.label)),
    }
  }

  const userFields = {
    'firstName': {
      xs: 6,
      type: 'text',
      default: '',
      label: 'First Name'
    },
    'lastName': {
      xs: 6,
      type: 'text',
      default: '',
      label: 'Last Name'
    },
    'email': {
      type: 'text',
      default: '',
      label: 'Email'
    }
  }

  const getLayout = (modelType) => {
    switch(modelType){
      case 'location':
        return Object.keys(locationFields).map(field => { return { field: field, xs: locationFields[field].xs ? locationFields[field].xs : 12 } })
      case 'user':
        return Object.keys(userFields).map(field => { return { field: field, xs: userFields[field].xs ? userFields[field].xs : 12 } })
      default:
        return Object.keys(organizationFields).map(field => { return { field: field, xs: organizationFields[field].xs ? organizationFields[field].xs : 12 } })
    }
  }

  const getFields = (modelType) => {
    let fieldsToReturn = organizationFields
    switch(modelType){
      case 'location':
        fieldsToReturn = locationFields
        break
      case 'user':
        fieldsToReturn = userFields
        break
      default:
        fieldsToReturn = organizationFields
        break
    }
    return fieldsToReturn
  }

  return <Dialog onClose={handleClose} aria-labelledby="simple-dialog-title" open={openForm} fullWidth={true} maxWidth="md" datacy={'editOrganizationModal'}>
        <DialogTitle id="simple-dialog-title">
          {selectedOrgModel.id ? `Edit ${selectedOrgModel.name}` : `New Organization, Location, User`}
        </DialogTitle>
        <SimpleModelForm
          spacing={1}
          isSaving={isSaving}
          model={selectedOrgModel}
          onClose={handleClose}
          layout = {getLayout('org')}
          fields = {getFields('org')}
          actions = {!locationModel ?[
            {
              'title': 'Cancel',
              'type': 'button',
              'action': handleClose,
            },
            {
              'title': 'Save',
              'type': 'submit',
            }
          ] : null}
        />
        {!!locationModel && <SimpleModelForm
          spacing={1}
          isSaving={isSaving}
          model={locationModel}
          onClose={handleClose}
          layout = {getLayout('location')}
          fields = {getFields('location')}
        />}
        {!!userModel && <SimpleModelForm
          spacing={1}
          isSaving={isSaving}
          model={userModel}
          onClose={handleClose}
          actions = {[
            {
              'title': 'Cancel',
              'type': 'button',
              'action': handleClose,
            },
            {
              'title': 'Save',
              'type': 'button',
              'action': handleSaveClick
            }
          ]}
          layout = {getLayout('user')}
          fields = {getFields('user')}
        />}
      </Dialog>
};

const OrganizationPageView = (props) => {
  const [openForm, setOpenForm] = useState(false);
  const [openAction, setOpenAction] = useState(false);
  const [selectedOrgModel, setSelectedOrgModel] = useState(false);
  const classes = useStyles();

  const {
    orgId, orgs, isFetching, orgsError, getOrgs, handleDeleteDone, sessionOrgId
  } = props;

  let org;
  if (!isFetching && !orgsError) {
    org = orgs.find(org => org.id === orgId);
  }

  // fetches all the orgs
  useEffect(() => {
    if (sessionOrgId !== 'default') {
      getOrgs();
    }
  }, [getOrgs, sessionOrgId]);

  const handleEditOrgClick = () => {
    setSelectedOrgModel(org);
    setOpenForm(true);
  };

  const handleClose = (action = null) => {
    setOpenForm(false);
    setSelectedOrgModel(false);
    getOrgs();
  };

  const handleDeleteOrgClick = () => {
    setSelectedOrgModel(org);
    setOpenAction(true);
  };

  const handleDelete = () => {
    selectedOrgModel.delete().then(() => {
      handleDeleteDone();
    });
  };

  const leftHeader = {
    component: 'th',
    width: 1,
    variant: 'head',
    className: classes.tableLeftHeaderCell,
  }

  return (
    <div className='dashboard organization-page'>
      <div id="organization-information">
        {isFetching ? <LoadingTable isMaterial />
        :
        <Paper>
          <Toolbar variant="dense" className={classes.toolBar}>
            <Grid container alignItems="center" className={classes.titleBar}>
              <Typography variant="h6" id="tableTitle" component="div">
                Organization Details
              </Typography>
              <Divider orientation="vertical" flexItem />
              <Button
                size="small"
                color="primary"
                onClick={handleEditOrgClick}
                startIcon={<EditIcon />}
                dataCy={'simpleTableRowIconButton'}
              >
                Edit
              </Button>
              {handleDeleteDone && false ?
              <Button
                size="small"
                color="primary"
                onClick={handleDeleteOrgClick}
                startIcon={<DeleteIcon />}
              >
                Delete this organization
              </Button>
              : ''}
            </Grid>
          </Toolbar>
          <TableContainer>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell {...leftHeader}>Name:</TableCell>
                  <TableCell>{org?.name}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell {...leftHeader}>Short Name:</TableCell>
                  <TableCell>{org?.shortName}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell {...leftHeader}>Web Domain:</TableCell>
                  <TableCell>{org?.webUrl}</TableCell>
                </TableRow>
                {!!props.superadmin && <TableRow>
                  <TableCell {...leftHeader}>Account Type:</TableCell>
                  <TableCell>{(ORG_TYPES.find(ot => ot.value === org?.orgType) || {label: org?.orgType}).label}</TableCell>
                </TableRow>}
                {!!props.superadmin && <TableRow>
                  <TableCell {...leftHeader}>Market:</TableCell>
                  <TableCell>{(MARKETS.find(m => m.value === org?.market) || {label: org?.market}).label}</TableCell>
                </TableRow>}
                {!!props.superadmin && <TableRow>
                  <TableCell {...leftHeader}>Internal Notes:</TableCell>
                  <TableCell>{org?.notes}</TableCell>
                </TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        }

        <ActionDialog
          open={openAction}
          title={`Delete ${selectedOrgModel.name}`}
          description='Are you sure you want to delete this organization?'
          actions = {[
            {
              'title': 'Cancel',
              'action': () => setOpenAction(false)
            },
            {
              'title': 'Delete',
              'action': handleDelete
            }
          ]}
        />

        <EditOrgDialog
          openForm={openForm}
          handleClose={handleClose}
          isSuperadmin={!!props.superadmin}
          selectedOrgModel={selectedOrgModel}
        />
      </div>
    </div>
  );
};

export const OrganizationComponent = OrganizationPageView;

const mapStateToProps = (state) => {
  const {
    orgs = {},
    isFetching = true,
    error,
  } = state.org.listOrgs;

  const isSuperadmin = state?.session?.settings?.user?.superadmin || false
  
  return {
    orgs,
    isFetching,
    orgsError: error,
    superadmin: isSuperadmin,
    sessionOrgId: state.page.payload.orgId
  };
};

const mapDispatchToProps = dispatch => ({
  getOrgs: () => dispatch(getOrgs()),
});

OrganizationPageView.propTypes = {
  orgs: PropTypes.array,
  payload: PropTypes.shape({}),
};

OrganizationPageView.defaultProps = {
  orgs: [],
  isFetching: true,
  orgsError: null,
  payload: { toplevel: 'settings', secondlevel: 'organization' },
};

export default connect(mapStateToProps, mapDispatchToProps)(OrganizationPageView);
